// TEMPORARY upload-receiver A/B experiment (docs/measurement-research.md §14). Delete with the receiver.
import { captureEnvironment, newRunId, type EnvironmentInfo } from './environment';

/**
 * Fixed method, identical for both targets:
 * - 4 parallel request loops (staggered 50 ms), each POSTing fixed-size bodies back to back.
 * - Each loop starts at 512 KiB and doubles its body size (max 16 MiB) after any request faster than 300 ms.
 * - The first 1 s is ramp-up; throughput is measured over the following 4 s window.
 * - No new requests start after the window; in-flight ones are allowed to finish.
 * - Only completed requests count. Each contributes the share of its bytes whose duration falls inside the window
 *   (assumes a constant rate within a request). Upload progress events are not used.
 * - Bodies are Blob slices: Chromium spends ~1 s per 16 MiB copying string/ArrayBuffer bodies before the request
 *   even starts, which would distort wall-clock timing (measured 2026-10-02, docs §14).
 */
export interface UploadMethod {
  streams: number;
  streamStaggerMs: number;
  rampUpMs: number;
  windowMs: number;
  initialChunkBytes: number;
  maxChunkBytes: number;
  growIfFasterThanMs: number;
}

export const UPLOAD_AB_METHOD = {
  streams: 4,
  streamStaggerMs: 50,
  rampUpMs: 1000,
  windowMs: 4000,
  initialChunkBytes: 512 * 1024,
  maxChunkBytes: 16 * 1024 * 1024,
  growIfFasterThanMs: 300,
  pauseBetweenTargetsMs: 1500,
} as const satisfies UploadMethod & { pauseBetweenTargetsMs: number };

/** Fly trial machines stop after 5 min of runtime; a run is only started with enough runtime left. */
export const RECEIVER_READINESS = {
  healthTimeoutMs: 30_000,
  healthRetryMs: 500,
  perAttemptTimeoutMs: 10_000,
  trialRuntimeLimitMs: 300_000,
  minRemainingRuntimeMs: 45_000,
} as const;

export interface CompletedRequest {
  startMs: number;
  endMs: number;
  bytes: number;
}

export function bytesInWindow(requests: CompletedRequest[], windowStart: number, windowEnd: number): number {
  let total = 0;
  for (const r of requests) {
    const duration = r.endMs - r.startMs;
    if (duration <= 0) continue;
    const overlap = Math.min(r.endMs, windowEnd) - Math.max(r.startMs, windowStart);
    if (overlap > 0) total += (r.bytes * overlap) / duration;
  }
  return total;
}

/** One completed request, times relative to the start of that target's measurement. */
export interface TimelineEntry extends CompletedRequest {
  stream: number;
}

export function windowMbps(timeline: CompletedRequest[], fromMs: number, toMs: number): number {
  return (bytesInWindow(timeline, fromMs, toMs) * 8) / ((toMs - fromMs) / 1000) / 1e6;
}

/** Throughput per 1-second bin (same proration as the window), for spotting ramp-up in exported runs. */
export function perSecondMbps(timeline: CompletedRequest[]): number[] {
  const lastEnd = timeline.reduce((max, r) => Math.max(max, r.endMs), 0);
  return Array.from({ length: Math.ceil(lastEnd / 1000) }, (_, s) => windowMbps(timeline, s * 1000, (s + 1) * 1000));
}

/** Waits needed (ms) before a fresh machine should be woken, or 0 if the current one has enough runtime left. */
export function waitForFreshMachineMs(uptimeMs: number): number {
  const remaining = RECEIVER_READINESS.trialRuntimeLimitMs - uptimeMs;
  return remaining >= RECEIVER_READINESS.minRemainingRuntimeMs ? 0 : Math.max(0, remaining) + 3000;
}

export type UploadTargetName = 'worker' | 'receiver';

export interface UploadTargetResult {
  target: UploadTargetName;
  url: string;
  mbps: number | null;
  attributedBytes: number;
  requests: number;
  maxChunkBytes: number;
  totalMs: number;
  protocols: string[];
  errors: string[];
  /** Diagnostic only: every completed request (times relative to this target's start). */
  timeline: TimelineEntry[];
}

export interface ReceiverHealth {
  ok: boolean;
  bootId: string | null;
  uptimeMs: number | null;
  region: string | null;
  error?: string;
}

export interface UploadAbRun {
  schema: 'measurement-spike/v1';
  kind: 'upload-ab';
  id: string;
  label: string;
  startedAt: string;
  environment: EnvironmentInfo;
  method: typeof UPLOAD_AB_METHOD;
  order: UploadTargetName[];
  cloudflareColo: string | null;
  receiverRegion: string | null;
  receiverWakeMs: number | null;
  receiverBootId: string | null;
  receiverUptimeAtStartMs: number | null;
  receiverPingMs: number | null;
  fastComUploadMbps: number | null;
  results: UploadTargetResult[];
  /** Invalid runs keep their raw data for debugging but must not be used as measurements. */
  status: 'valid' | 'invalid';
  invalidReasons: string[];
  wallClockMs: number;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export async function checkHealth(receiverUrl: string): Promise<ReceiverHealth> {
  try {
    const response = await fetch(`${receiverUrl}/health?r=${Math.random()}`, {
      cache: 'no-store',
      credentials: 'omit',
      signal: AbortSignal.timeout(RECEIVER_READINESS.perAttemptTimeoutMs),
    });
    await response.arrayBuffer();
    const uptime = response.headers.get('X-Receiver-Uptime-Ms');
    return {
      ok: response.status === 200,
      bootId: response.headers.get('X-Receiver-Boot'),
      uptimeMs: uptime === null ? null : Number(uptime),
      region: response.headers.get('X-Served-Region'),
      ...(response.status === 200 ? {} : { error: `HTTP ${response.status}` }),
    };
  } catch (error) {
    return { ok: false, bootId: null, uptimeMs: null, region: null, error: String(error) };
  }
}

export async function waitUntilHealthy(receiverUrl: string): Promise<ReceiverHealth> {
  const deadline = performance.now() + RECEIVER_READINESS.healthTimeoutMs;
  let last: ReceiverHealth = { ok: false, bootId: null, uptimeMs: null, region: null, error: 'not attempted' };
  while (performance.now() < deadline) {
    last = await checkHealth(receiverUrl);
    if (last.ok && last.bootId) return last;
    await sleep(RECEIVER_READINESS.healthRetryMs);
  }
  return last;
}

let uploadBlob: Blob | undefined;

export async function measureUpload(
  target: UploadTargetName,
  url: string,
  expectedBootId: string | null,
  m: UploadMethod = UPLOAD_AB_METHOD,
): Promise<UploadTargetResult> {
  if (!uploadBlob || uploadBlob.size < m.maxChunkBytes) {
    uploadBlob = new Blob([new Uint8Array(m.maxChunkBytes)], { type: 'text/plain' });
  }
  const body = uploadBlob;
  // The default resource-timing buffer (~250 entries) fills within a few runs, after which protocols go unreported.
  performance.clearResourceTimings();
  performance.setResourceTimingBufferSize(2000);
  const t0 = performance.now();
  const windowStart = t0 + m.rampUpMs;
  const windowEnd = windowStart + m.windowMs;
  const completed: CompletedRequest[] = [];
  const timeline: TimelineEntry[] = [];
  const errors: string[] = [];
  let maxChunk = 0;

  const loop = async (index: number) => {
    await sleep(index * m.streamStaggerMs);
    let size: number = m.initialChunkBytes;
    while (performance.now() < windowEnd && errors.length === 0) {
      const startMs = performance.now();
      try {
        const response = await fetch(url, {
          method: 'POST',
          body: body.slice(0, size, 'text/plain'),
          headers: { 'Content-Type': 'text/plain' },
          cache: 'no-store',
          credentials: 'omit',
        });
        await response.arrayBuffer();
        const endMs = performance.now();
        if (response.status !== 204) {
          errors.push(`HTTP ${response.status} on ${size} B`);
          return;
        }
        if (expectedBootId && response.headers.get('X-Receiver-Boot') !== expectedBootId) {
          errors.push('receiver restarted during the run (boot ID changed)');
          return;
        }
        completed.push({ startMs, endMs, bytes: size });
        timeline.push({ stream: index, startMs: startMs - t0, endMs: endMs - t0, bytes: size });
        maxChunk = Math.max(maxChunk, size);
        if (endMs - startMs < m.growIfFasterThanMs && size < m.maxChunkBytes) size *= 2;
      } catch (error) {
        errors.push(String(error));
        return;
      }
    }
  };
  await Promise.all(Array.from({ length: m.streams }, (_, i) => loop(i)));

  const attributedBytes = bytesInWindow(completed, windowStart, windowEnd);
  const protocols = [
    ...new Set(
      performance
        .getEntriesByName(new URL(url, location.href).href)
        .map((e) => (e as PerformanceResourceTiming).nextHopProtocol || 'not reported'),
    ),
  ];
  return {
    target,
    url,
    mbps: errors.length ? null : (attributedBytes * 8) / (m.windowMs / 1000) / 1e6,
    attributedBytes,
    requests: completed.length,
    maxChunkBytes: maxChunk,
    totalMs: performance.now() - t0,
    protocols,
    errors,
    timeline,
  };
}

export async function cloudflareColo(): Promise<string | null> {
  try {
    const text = await (await fetch(`/cdn-cgi/trace?r=${Math.random()}`, { cache: 'no-store' })).text();
    // Only the data-centre code is read; the rest of the trace (including the client IP) is discarded.
    return /^colo=(\w+)$/m.exec(text)?.[1] ?? null;
  } catch {
    return null;
  }
}

async function receiverPingMs(receiverUrl: string, bootId: string): Promise<{ pingMs: number | null; restarted: boolean }> {
  const samples: number[] = [];
  let restarted = false;
  for (let i = 0; i < 5; i++) {
    const t = performance.now();
    const response = await fetch(`${receiverUrl}/ping?r=${Math.random()}`, { cache: 'no-store', credentials: 'omit' });
    await response.arrayBuffer();
    samples.push(performance.now() - t);
    if (response.headers.get('X-Receiver-Boot') !== bootId) restarted = true;
  }
  samples.sort((a, b) => a - b);
  return { pingMs: samples[Math.floor(samples.length / 2)] ?? null, restarted };
}

export async function runUploadAb(options: {
  label: string;
  receiverUrl: string;
  fastComUploadMbps: number | null;
  receiverFirst: boolean;
  onStatus?: (message: string) => void;
}): Promise<UploadAbRun> {
  const status = options.onStatus ?? (() => {});
  const t0 = performance.now();
  const startedAt = new Date().toISOString();
  const order: UploadTargetName[] = options.receiverFirst ? ['receiver', 'worker'] : ['worker', 'receiver'];
  const urls: Record<UploadTargetName, string> = { worker: '/up?ab=1', receiver: `${options.receiverUrl}/up` };
  const invalidReasons: string[] = [];
  const results: UploadTargetResult[] = [];

  const colo = await cloudflareColo();

  status('Waking the upload receiver…');
  const wakeStart = performance.now();
  let health = await waitUntilHealthy(options.receiverUrl);
  const wait = health.ok && health.uptimeMs !== null ? waitForFreshMachineMs(health.uptimeMs) : 0;
  if (wait > 0) {
    status(`Receiver is about to hit the Fly trial's 5-minute limit; waiting ${Math.round(wait / 1000)} s for a fresh machine…`);
    await sleep(wait);
    health = await waitUntilHealthy(options.receiverUrl);
  }
  const wakeMs = performance.now() - wakeStart;
  let pingMs: number | null = null;

  if (!health.ok || !health.bootId) {
    invalidReasons.push(`receiver not healthy before the test: ${health.error ?? 'no boot ID'}`);
  } else {
    const ping = await receiverPingMs(options.receiverUrl, health.bootId);
    pingMs = ping.pingMs;
    if (ping.restarted) invalidReasons.push('receiver restarted during the pre-test ping');
  }

  if (!invalidReasons.length) {
    for (const [i, target] of order.entries()) {
      if (i > 0) await sleep(UPLOAD_AB_METHOD.pauseBetweenTargetsMs);
      status(`Measuring upload to the ${target === 'worker' ? 'Cloudflare Worker' : 'receiver'}…`);
      const result = await measureUpload(target, urls[target], target === 'receiver' ? health.bootId : null);
      results.push(result);
      for (const error of result.errors) invalidReasons.push(`${target}: ${error}`);
    }
    const after = await checkHealth(options.receiverUrl);
    if (!after.ok) invalidReasons.push(`receiver unhealthy after the test: ${after.error ?? 'unknown'}`);
    else if (after.bootId !== health.bootId) invalidReasons.push('receiver restarted during the run (boot ID changed by the final health check)');
  }

  return {
    schema: 'measurement-spike/v1',
    kind: 'upload-ab',
    id: newRunId(),
    label: options.label,
    startedAt,
    environment: captureEnvironment(),
    method: UPLOAD_AB_METHOD,
    order,
    cloudflareColo: colo,
    receiverRegion: health.region,
    receiverWakeMs: wakeMs,
    receiverBootId: health.bootId,
    receiverUptimeAtStartMs: health.uptimeMs,
    receiverPingMs: pingMs,
    fastComUploadMbps: options.fastComUploadMbps,
    results,
    status: invalidReasons.length ? 'invalid' : 'valid',
    invalidReasons,
    wallClockMs: performance.now() - t0,
  };
}
